import { useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { toast } from "sonner";
import { Download, Github, Linkedin } from "lucide-react";
import { APPS } from "@/data/windowRegistry";
import { useWindows } from "@/context/WindowContext";
import { downloadResume } from "@/utils/download";
import { EASE } from "@/components/ui/Reveal";

function DockIcon({ mouseX, label, testid, tile, Icon, open, onClick }) {
  const ref = useRef(null);
  const [hover, setHover] = useState(false);
  const distance = useTransform(mouseX, (v) => {
    const b = ref.current ? ref.current.getBoundingClientRect() : null;
    return b ? v - (b.x + b.width / 2) : Infinity;
  });
  const scaleRaw = useTransform(distance, [-120, 0, 120], [1, 1.2, 1]);
  const scale = useSpring(scaleRaw, { stiffness: 300, damping: 20 });
  const y = useTransform(scale, [1, 1.2], [0, -4]);

  return (
    <motion.button
      ref={ref}
      data-testid={testid}
      aria-label={label}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ scale, y, transformOrigin: "bottom center" }}
      className="relative flex flex-col items-center outline-none"
    >
      <AnimatePresence>
        {hover && (
          <motion.span
            initial={{ opacity: 0, y: 6, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.9 }}
            className="pointer-events-none absolute -top-9 whitespace-nowrap rounded-md border border-white/10 bg-black/85 px-2.5 py-1 font-mono text-[10px] text-white"
            data-testid={`${testid}-tooltip`}
          >
            {label}
          </motion.span>
        )}
      </AnimatePresence>
      <span className={`flex h-9 w-9 items-center justify-center rounded-[11px] bg-gradient-to-br ${tile} shadow-lg shadow-black/50 ring-1 ring-white/15 transition-shadow duration-200 hover:shadow-blue-900/40`}>
        <Icon size={17} className="text-white drop-shadow" strokeWidth={1.8} />
      </span>
      <span className={`mt-0.5 h-1 w-1 rounded-full transition-opacity duration-200 ${open ? "bg-white/85 opacity-100" : "opacity-0"}`} />
    </motion.button>
  );
}

export default function Dock() {
  const { wins, open, focus, minimize, topZ } = useWindows();
  const mouseX = useMotionValue(Infinity);
  const apps = APPS.filter((a) => a.inDock);

  const onAppClick = (id) => {
    const w = wins[id];
    if (!w.open || w.minimized) open(id);
    else if (w.z === topZ) minimize(id);
    else focus(id);
  };

  return (
    <nav className="fixed inset-x-0 bottom-3 z-[800] flex justify-center px-3" data-testid="dock">
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.35, duration: 0.6, ease: EASE }}
        onMouseMove={(e) => mouseX.set(e.clientX)}
        onMouseLeave={() => mouseX.set(Infinity)}
        className="glass flex max-w-full items-end gap-1.5 rounded-2xl px-2.5 py-2 sm:gap-2.5 max-md:overflow-x-auto"
      >
          {apps.map((a) => (
            <DockIcon key={a.id} mouseX={mouseX} label={a.dockLabel} testid={`dock-item-${a.id}`} tile={a.tile} Icon={a.icon} open={wins[a.id].open} onClick={() => onAppClick(a.id)} />
          ))}
          <span className="mx-1 h-9 w-px self-center bg-white/10" />
          <DockIcon mouseX={mouseX} label="GitHub" testid="dock-link-github" tile="from-slate-500 to-slate-800" Icon={Github} onClick={() => window.open("https://github.com/anishravi", "_blank", "noopener")} />
          <DockIcon mouseX={mouseX} label="LinkedIn" testid="dock-link-linkedin" tile="from-sky-500 to-sky-800" Icon={Linkedin} onClick={() => window.open("https://linkedin.com/in/anishravi-swe", "_blank", "noopener")} />
          <DockIcon
            mouseX={mouseX}
            label="Resume PDF"
            testid="dock-link-resume"
            tile="from-emerald-500 to-emerald-700"
            Icon={Download}
            onClick={() => {
              downloadResume();
              toast("Resume downloading — AnishResume_WA.pdf");
            }}
          />
        </motion.div>
      </nav>
  );
}
